const DEFAULT_OUTPUT_SAMPLE_RATE = 16000;

function toFloat32Array(input) {
  return input instanceof Float32Array ? input : new Float32Array(input);
}

export function createPcm16Resampler(
  inputSampleRate,
  outputSampleRate = DEFAULT_OUTPUT_SAMPLE_RATE
) {
  if (inputSampleRate <= 0 || outputSampleRate <= 0) {
    throw new RangeError('Audio sample rates must be positive.');
  }

  const step = inputSampleRate / outputSampleRate;
  let pending = new Float32Array(0);
  let position = 0;

  function push(chunk) {
    const incoming = toFloat32Array(chunk);
    if (!incoming.length) return new Int16Array(0);

    const samples = new Float32Array(pending.length + incoming.length);
    samples.set(pending);
    samples.set(incoming, pending.length);

    const output = [];
    while (position + 1 < samples.length) {
      const leftIndex = Math.floor(position);
      const rightIndex = leftIndex + 1;
      const fraction = position - leftIndex;
      const value =
        samples[leftIndex] +
        (samples[rightIndex] - samples[leftIndex]) * fraction;
      const clamped = Math.max(-1, Math.min(1, value));
      output.push(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
      position += step;
    }

    const consumed = Math.floor(position);
    pending = samples.slice(consumed);
    position -= consumed;
    return Int16Array.from(output);
  }

  return { push };
}

export async function createGeminiLiveAudioInput({
  audioContext,
  stream,
  isMuted,
  onPcm16,
  workletUrl = '/gemini-live-mic-processor.js',
  AudioWorkletNodeCtor = globalThis.AudioWorkletNode,
}) {
  const source = audioContext.createMediaStreamSource(stream);
  const resampler = createPcm16Resampler(audioContext.sampleRate);
  let processor = null;

  const forward = (samples) => {
    if (isMuted()) return;
    const pcm16 = resampler.push(samples);
    if (pcm16.length) onPcm16(pcm16);
  };

  if (audioContext.audioWorklet && AudioWorkletNodeCtor) {
    try {
      await audioContext.audioWorklet.addModule(workletUrl);
      processor = new AudioWorkletNodeCtor(
        audioContext,
        'gemini-live-mic-processor'
      );
      processor.port.onmessage = (event) => forward(event.data);
      source.connect(processor);
      // Keep the worklet in the active audio graph. Its processor writes
      // silence to this output, so the microphone is never played to speakers.
      processor.connect(audioContext.destination);
    } catch {
      processor?.disconnect();
      processor = null;
    }
  }

  if (!processor) {
    // Safari versions without AudioWorklet support still get a functional
    // capture path. Resampling below keeps their native rate honest.
    processor = audioContext.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (event) => {
      forward(event.inputBuffer.getChannelData(0));
    };
    source.connect(processor);
    processor.connect(audioContext.destination);
  }

  return {
    stop() {
      if (processor?.port) processor.port.onmessage = null;
      if (processor) processor.onaudioprocess = null;
      processor?.disconnect();
      source.disconnect();
    },
  };
}
